/**
 * Pure derivations for the TIA report/full-record views (VER-299 P7a).
 *
 * Deliberately small — TIA has no per-item editing surface (no
 * `measures[]` analog), so unlike DPIA's `shared/` folder this holds
 * only display-shaping helpers, not mutation/link-hydration logic.
 */

import type { TiaRiskProfileData, TiaSourceRef } from '../../../../models/assessments/tia.model';
/** Defends the FE against a pre-VER-332-rewrite TIA record whose
 *  `risk_profile` predates `verdict_status`/`conditions`/
 *  `supplementary_measures`/`divergences` — those keys are simply
 *  absent from the stored blob. The BE now heals every read through
 *  `TiaRiskProfile`'s own Pydantic validator
 *  (`assessment_handler`'s `_heal_risk_profile_for_response`), so this
 *  should be a no-op in practice; kept as a second line of defense so
 *  `<app-tia-full-record>`/`<app-tia-report>` never throw on
 *  `.length`/`.toLowerCase()` if some other read path is ever added
 *  that skips that healer. Only defaults the fields that are new as of
 *  the rewrite — every other field already existed pre-rewrite. */
export function normalizeTiaRiskProfile(tia: TiaRiskProfileData): TiaRiskProfileData {
  return {
    ...tia,
    verdict_status: tia.verdict_status ?? 'CONDITIONAL',
    conditions: tia.conditions ?? [],
    supplementary_measures: tia.supplementary_measures ?? [],
    accepted_supplementary_measures: tia.accepted_supplementary_measures ?? [],
    divergences: tia.divergences ?? [],
  };
}

/** The three exempt outcomes complete instantly with no agent call —
 *  the profile carries only `gate_outcome`/`gate_rationale`/`verdict`,
 *  with `transfer_summary`/`residual_risk`/`recommended_safeguards`
 *  empty. Real (non-exempt) outcomes carry the full narrative.
 *
 *  Exempt status is determined SOLELY from `gate_outcome` — callers
 *  must not substitute "narrative fields are empty" as a proxy for
 *  this. A malformed/incomplete real (non-exempt) profile with no
 *  `transfer_summary`/`residual_risk` is a data-quality problem, not
 *  an exempt result, and must not be mislabelled as one. */
export function isExemptOutcome(
  gateOutcome: TiaRiskProfileData['gate_outcome'] | null | undefined
): boolean {
  return (
    gateOutcome === 'EEA_NO_TRANSFER' ||
    gateOutcome === 'ADEQUACY_EXEMPT' ||
    gateOutcome === 'ART49_EXEMPT'
  );
}

/** Recommended-safeguards list, de-duplicated and with blanks dropped —
 *  defensive against an LLM emitting a stray empty string. */
export function safeguardRows(tia: TiaRiskProfileData | null | undefined): string[] {
  const safeguards = tia?.recommended_safeguards ?? [];
  const seen = new Set<string>();
  const rows: string[] = [];
  for (const s of safeguards) {
    const trimmed = s?.trim();
    if (trimmed && !seen.has(trimmed)) {
      seen.add(trimmed);
      rows.push(trimmed);
    }
  }
  return rows;
}

/** Source citations, de-duplicated by (title, url) pair. */
export function dedupeSources(sources: TiaSourceRef[] | null | undefined): TiaSourceRef[] {
  const seen = new Set<string>();
  const rows: TiaSourceRef[] = [];
  for (const s of sources ?? []) {
    const key = `${s.title}::${s.url ?? ''}`;
    if (!seen.has(key)) {
      seen.add(key);
      rows.push(s);
    }
  }
  return rows;
}

/** VER-332 §1.1 — the Risk & Sign-off tab's 5-item completion checklist,
 *  replacing the old "N items before complete" summary counters. Derived
 *  live from record state every time (no stored list), each row linking
 *  into the section it refers to.
 *
 *  Row 3 ("Supplementary measures confirmed") is shown only when the
 *  pinned country's RAG rating is Medium/High, per the ticket. Its
 *  *completion* criterion still reuses the existing BE approve-gate
 *  signal (`SUPPLEMENTARY_REQUIRED`, driven by `gate_outcome` needing a
 *  safeguard) rather than a RAG-driven completion rule of its own — the
 *  ticket frames visibility and the BE's actual approve-gate enforcement
 *  along two different axes (RAG rating vs. gate_outcome) that aren't
 *  reconciled server-side; changing that is a BE change, out of scope
 *  here. Using the real enforcement signal for "done" avoids the
 *  checklist showing complete while approve would still 422. */
export interface TiaCompletionChecklistRow {
  id: string;
  labelKey: string;
  done: boolean;
  statusKey: string;
  statusParams?: Record<string, string | number>;
  sectionId: string;
}

interface TiaCompletionChecklistInput {
  countryConfirmedAt: string | null | undefined;
  ragRating: 'LOW' | 'MEDIUM' | 'HIGH' | null | undefined;
  acceptedSafeguardsCount: number;
  recommendedSafeguardsCount: number;
  acceptedSupplementaryMeasuresCount: number;
  recommendedSupplementaryMeasuresCount: number;
  verdictRecorded: boolean;
  controllerSigned: boolean;
  controllerSignedByName: string | null;
}

export function buildTiaCompletionChecklist(
  input: TiaCompletionChecklistInput
): TiaCompletionChecklistRow[] {
  const rows: TiaCompletionChecklistRow[] = [
    {
      id: 'country_risk',
      labelKey: 'tia.checklist.country_risk',
      done: !!input.countryConfirmedAt,
      statusKey: input.countryConfirmedAt
        ? 'tia.checklist.country_risk_done'
        : 'tia.checklist.country_risk_pending',
      sectionId: 'tia-country-risk',
    },
    {
      id: 'safeguards',
      labelKey: 'tia.checklist.safeguards',
      done: input.recommendedSafeguardsCount === 0 || input.acceptedSafeguardsCount > 0,
      statusKey: 'tia.checklist.safeguards_status',
      statusParams: {
        confirmed: input.acceptedSafeguardsCount,
        total: Math.max(input.recommendedSafeguardsCount, input.acceptedSafeguardsCount),
      },
      sectionId: 'tia-safeguards',
    },
  ];

  const ragNeedsMeasures = input.ragRating === 'MEDIUM' || input.ragRating === 'HIGH';
  if (ragNeedsMeasures) {
    const supplementaryDone =
      input.recommendedSupplementaryMeasuresCount === 0 ||
      input.acceptedSupplementaryMeasuresCount > 0;
    rows.push({
      id: 'supplementary_measures',
      labelKey: 'tia.checklist.supplementary_measures',
      done: supplementaryDone,
      statusKey: supplementaryDone
        ? 'tia.checklist.supplementary_measures_status'
        : 'tia.checklist.supplementary_measures_pending',
      statusParams: supplementaryDone
        ? {
            confirmed: input.acceptedSupplementaryMeasuresCount,
            total: Math.max(
              input.recommendedSupplementaryMeasuresCount,
              input.acceptedSupplementaryMeasuresCount
            ),
          }
        : { rating: input.ragRating ?? '' },
      sectionId: 'tia-supplementary-measures',
    });
  }

  rows.push(
    {
      id: 'conclusion',
      labelKey: 'tia.checklist.conclusion',
      done: input.verdictRecorded,
      statusKey: input.verdictRecorded
        ? 'tia.checklist.conclusion_done'
        : 'tia.checklist.conclusion_pending',
      sectionId: 'tia-conclusion',
    },
    {
      id: 'sign_off',
      labelKey: 'tia.checklist.sign_off',
      done: input.controllerSigned,
      statusKey: input.controllerSigned
        ? 'tia.checklist.sign_off_done'
        : 'tia.checklist.sign_off_pending',
      statusParams: input.controllerSignedByName
        ? { name: input.controllerSignedByName }
        : undefined,
      sectionId: 'tia-sign-off',
    }
  );

  return rows;
}
